import graphql__7b318f5f0e8d663558f6 from "./__generated__/ApiKeyManager_apiKeys.graphql.ts";
import graphql__a5e156b4b43a9810539b from "./__generated__/ApiKeyManagerQuery.graphql.ts";import {
  createColumnHelper,
  DEFAULT_TABLE_HEADER_ROW_HEIGHT,
  DEFAULT_TABLE_ROW_HEIGHT,
  icons,
  Input,
  LinkButton,
  PaginationState,
  SortingState,
  Table,
  TableColumnDef,
  TablePaginationControls,
} from '@portal/components'
import graphql from 'babel-plugin-relay/macro'
import EmptyState from 'components/emptyState/EmptyState'
import dayjs from 'dayjs'
import { useCallback, useMemo, useState } from 'react'
import { useFragment, useLazyLoadQuery } from 'react-relay/hooks'
import AutoSizer from 'react-virtualized-auto-sizer'
import { DeepWritable } from 'ts-essentials'

import {
  ApiKeyManager_apiKeys$data,
  ApiKeyManager_apiKeys$key,
} from './__generated__/ApiKeyManager_apiKeys.graphql'
import { ApiKeyManagerQuery } from './__generated__/ApiKeyManagerQuery.graphql'
import ApiKeyActionMenu from './ApiKeyActionMenu'
import ApiKeyDeletionModal from './ApiKeyDeletionModal'
import ApiKeyInspector from './ApiKeyInspector'

const { Search } = icons

interface Props {
  appId: string
}

const apiKeyFragment = graphql__7b318f5f0e8d663558f6

const getAppKeysQuery = graphql__a5e156b4b43a9810539b

type RawApiKey = NonNullable<ApiKeyManager_apiKeys$data>['apiKeys'][number]
export type ApiKey = DeepWritable<RawApiKey>

const columnHelper = createColumnHelper<ApiKey>()
const baseColumns = [
  columnHelper.accessor('description', {
    header: 'Description',
    cell: ({ getValue }) => (
      <span data-testid="apiKeyDescription" className="break-all">
        {getValue()}
      </span>
    ),
  }),
  columnHelper.accessor('expiresAt', {
    header: 'Expiry date',
    cell: ({ getValue }) => (
      <span data-testid="apiKeyExpiresAt" className="break-all">
        {dayjs(getValue()).format('LL')}
      </span>
    ),
    sortingFn: ({ original: a }, { original: b }) =>
      dayjs(a.expiresAt).diff(dayjs(b.expiresAt)),
  }),
]

// The max height of the table is 5 rows + the header row. Everything beyond that
// will be scrollable. This is mostly sylistic, so it doesn't grow too large.
const MAX_HEIGHT =
  DEFAULT_TABLE_HEADER_ROW_HEIGHT + DEFAULT_TABLE_ROW_HEIGHT * 5

export default function ApiKeyManager({ appId }: Props) {
  const [filter, setFilter] = useState('')
  const [sorting, setSorting] = useState<SortingState>([])
  const [apiKeyForDeletion, setApiKeyForDeletion] = useState<ApiKey>()
  const [apiKeyForInspector, setApiKeyForInspector] = useState<ApiKey>()

  const { viewer } = useLazyLoadQuery<ApiKeyManagerQuery>(getAppKeysQuery, {
    appId,
  })
  const viewerCanCreateApiKey = viewer.appById?.viewerCanCreateApiKey ?? false
  const viewerCanDeleteApiKey = viewer.appById?.viewerCanDeleteApiKey ?? false
  const appById = useFragment<ApiKeyManager_apiKeys$key>(
    apiKeyFragment,
    viewer.appById,
  )

  const apiKeys = (appById?.apiKeys || []) as ApiKey[]

  const columns = useMemo<TableColumnDef<ApiKey>[]>(
    () => [
      ...baseColumns,
      columnHelper.display({
        id: 'actions',
        cell: ({ row: { original: apiKey } }) => (
          <ApiKeyActionMenu
            viewerCanDelete={viewerCanDeleteApiKey}
            onSelectForDeletion={() =>
              viewerCanDeleteApiKey ? setApiKeyForDeletion(apiKey) : undefined
            }
            onSelectForInspection={() => setApiKeyForInspector(apiKey)}
          />
        ),
        size: 30,
        meta: {
          isActionColumn: true,
        },
        enableSorting: false,
      }),
    ],
    [viewerCanDeleteApiKey],
  )

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 5,
  })

  const { pageIndex, pageSize } = pagination
  // NOTE: Client-side pagination should be fine for this use case, I don't foresee large datasets.
  // If this becomes a problem, we can revisit this by adding offset/limit to the query.
  const totalCount = apiKeys.length
  const pageCount = useMemo(
    () => Math.ceil(totalCount / pageSize),
    [totalCount, pageSize],
  )

  const showPagination = totalCount || (!totalCount && !!filter.length)

  const handleSetPage = useCallback(
    (page: number) => {
      setPagination((prev) => ({ ...prev, pageIndex: page - 1 }))
    },
    [setPagination],
  )

  return (
    <>
      {viewerCanDeleteApiKey && (
        <ApiKeyDeletionModal
          apiKey={apiKeyForDeletion}
          onClose={() => setApiKeyForDeletion(undefined)}
          onDeleted={() => setApiKeyForDeletion(undefined)}
        />
      )}
      <ApiKeyInspector
        apiKey={apiKeyForInspector}
        onClose={() => setApiKeyForInspector(undefined)}
      />
      <div className="w-full">
        <div className="flex h-12 items-center gap-x-2 px-2">
          <Input
            className="relative w-full"
            leadingIcon={<Search className="h-4 w-4 text-foreground-subtle" />}
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search API keys"
            disabled={apiKeys.length === 0 && !filter.length}
          />
          {viewerCanCreateApiKey && (
            <LinkButton variant="primary" to="api-keys/new">
              New API key
            </LinkButton>
          )}
        </div>

        <AutoSizer disableHeight>
          {({ width }: { width: number }) => (
            <Table
              columns={columns}
              data={apiKeys}
              width={width}
              height={(rowCount) =>
                rowCount > 0
                  ? Math.min(
                      MAX_HEIGHT,
                      DEFAULT_TABLE_HEADER_ROW_HEIGHT +
                        DEFAULT_TABLE_ROW_HEIGHT * rowCount,
                    )
                  : 156
              }
              activeOnHover={false}
              tableCellClassName="first:pl-4 first:sm:pl-6"
              tableHeaderCellClassName="first:pl-4 first:sm:pl-6"
              emptyStateRenderer={() => (
                <EmptyState
                  className="py-4"
                  message={
                    !!filter.length
                      ? 'No API keys match the current filter'
                      : 'No HTTP API keys yet'
                  }
                />
              )}
              options={{
                state: {
                  globalFilter: filter,
                  sorting,
                },
                onSortingChange: setSorting,
              }}
            />
          )}
        </AutoSizer>

        {showPagination && (
          <TablePaginationControls
            height={40}
            currentPage={pageIndex + 1}
            pageCount={pageCount}
            onSetPage={handleSetPage}
            centerContent={`${totalCount} API ${totalCount !== 1 ? 'keys' : 'key'}`}
          />
        )}
      </div>
    </>
  )
}
